import { postTrackingData } from "@/api/endpoints/track";
import { MENTIONS_SHEET_DATE_FORMAT } from "@/constants";
import { useInsightView } from "@/hooks/useInsightView";
import { omitPageParams } from "@/hooks/usePageParam";
import { EVENT } from "@/tracking_events";
import { getMCPMonitorTimeSeries } from "@/api/endpoints/searches";
import { useI18n } from "@/contexts/i18nContext";
import useTimezone from "@/hooks/useActiveTimezone";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/ui/chart";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetTitle,
} from "@components/ui/widget";
import {
  formatDateFromMCPMonitorTimeSeriesGranularity,
  getMCPMonitorFiltersFromSearchFilterV4,
  getMCPMonitorTimeSeriesGranularityFromDayDiff,
  getPeriodFromStartDateAndGranularity,
} from "@components/widgets/utils";
import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { ComponentProps } from "react";
import { generatePath, useHistory, useLocation } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";

const STACK_ORDER = ["neutral", "negative", "positive"] as const;

// Shares of the scored mentions, ordered top-down like the stack
function SentimentTooltipContent({
  payload,
  ...rest
}: ComponentProps<typeof ChartTooltipContent>) {
  const { formatNumber, formatPercent } = useI18n();

  const point = payload?.[0]?.payload;

  const scored = STACK_ORDER.reduce(
    (sum, key) => sum + Number(point?.[key] ?? 0),
    0
  );

  const total = scored + Number(point?.unknown ?? 0);

  return (
    <ChartTooltipContent
      {...rest}
      payload={payload
        ?.map((item) => {
          const count = Number(item.value);

          return {
            ...item,
            value: `${formatNumber(count)} (${formatPercent(
              scored ? count / scored : 0,
              0
            )})`,
          };
        })
        .reverse()}
      footer={
        <Trans>
          Sentiment finns tillgängligt för {formatNumber(scored)} av{" "}
          {formatNumber(total)}{" "}
          {plural(total, { one: "träff", other: "träffar" })}
        </Trans>
      }
    />
  );
}

export default function SentimentOverTimeWidget({
  searchUuid,
  diffInDays,
  orgUuid,
  filters,
  dayLink,
  ...rest
}: WidgetProps & {
  filters: SearchFilterV4WithDates;
  diffInDays: number;
  searchUuid: Uuid;
  orgUuid: Uuid;
  dayLink: string;
}) {
  const { formatDate } = useI18n();
  const { t } = useLingui();
  const timezone = useTimezone();

  // URL stte
  const history = useHistory();
  const insight = useInsightView();
  const location = useLocation();

  // Server state
  const granularity = getMCPMonitorTimeSeriesGranularityFromDayDiff(diffInDays);

  const {
    data: sentimentsTimeSeriesData,
    isLoading: isSentimentTimeSeriesLoading,
  } = useQuery({
    queryKey: [searchUuid, "sentiments-time-series", filters],
    queryFn: () =>
      getMCPMonitorTimeSeries(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        metric: "sentiment",
        granularity,
      }),
  });

  // Helpers
  const chartData = sentimentsTimeSeriesData?.ok
    ? sentimentsTimeSeriesData.data.points.map((point) => ({
        date: point.date,
        negative: point.negative,
        unknown: point.unknown,
        neutral: point.neutral,
        positive: point.positive,
      }))
    : undefined;

  const noSentimentValues = !chartData?.some((point) =>
    STACK_ORDER.some((key) => point[key])
  );

  const isEmpty =
    !isSentimentTimeSeriesLoading &&
    (!sentimentsTimeSeriesData?.ok || noSentimentValues);

  const chartConfig = {
    positive: {
      label: t`Positiva`,
      color: "var(--color-green-500)",
    },
    neutral: {
      label: t`Neutrala`,
      color: "var(--color-neutral-400)",
    },
    negative: {
      label: t`Negativa`,
      color: "var(--color-red-500)",
    },
  } satisfies ChartConfig;

  return (
    <Widget {...rest}>
      <WidgetHeader
        isLoading={isSentimentTimeSeriesLoading}
        skeletonClassName="h-11.5"
      >
        <div className="flex flex-col gap-0.5">
          <WidgetTitle>
            <Trans>Sentiment över tid</Trans>
          </WidgetTitle>

          <p className="text-muted-foreground text-sm">
            <Trans>
              Se hur positivt, neutralt och negativt sentiment utvecklas över
              tid.
            </Trans>
          </p>
        </div>
      </WidgetHeader>
      <WidgetContent
        isLoading={isSentimentTimeSeriesLoading}
        isEmpty={isEmpty}
        className="justify-end"
      >
        {chartData ? (
          <ChartContainer
            config={chartConfig}
            className="h-full w-full aspect-auto"
          >
            <AreaChart
              accessibilityLayer
              data={chartData}
              margin={{ top: 8 }}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                if (!e.activeLabel || typeof e.activeLabel !== "string") {
                  return;
                }

                const { startDate, endDate } =
                  getPeriodFromStartDateAndGranularity(
                    e.activeLabel,
                    granularity,
                    timezone
                  );

                postTrackingData(EVENT.WIDGET_CLICK, {
                  insight,
                  widget: "sentiment_over_time",
                  action: "data_point",
                });

                history.replace({
                  pathname: generatePath(dayLink, {
                    startDate: formatDate(
                      startDate,
                      MENTIONS_SHEET_DATE_FORMAT
                    ),
                    endDate: formatDate(endDate, MENTIONS_SHEET_DATE_FORMAT),
                    uuid: searchUuid,
                    orgUuid,
                  }),
                  search: omitPageParams(location.search, ["sheet_page"]),
                });
              }}
            >
              <defs>
                {STACK_ORDER.map((key) => (
                  <linearGradient
                    key={key}
                    id={`fillSentiment-${key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="10%"
                      stopColor={`var(--color-${key})`}
                      stopOpacity={0.6}
                    />
                    <stop
                      offset="90%"
                      stopColor={`var(--color-${key})`}
                      stopOpacity={0.01}
                    />
                  </linearGradient>
                ))}
              </defs>

              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) =>
                  formatDateFromMCPMonitorTimeSeriesGranularity(
                    value,
                    granularity,
                    formatDate
                  )
                }
                interval="preserveStartEnd"
              />

              <ChartTooltip
                labelFormatter={(value) =>
                  formatDateFromMCPMonitorTimeSeriesGranularity(
                    value as string,
                    granularity,
                    formatDate
                  )
                }
                content={<SentimentTooltipContent />}
              />

              <Area
                dataKey="neutral"
                fill="url(#fillSentiment-neutral)"
                stroke="var(--color-neutral)"
                isAnimationActive={false}
                stackId="a"
                type="bump"
              />
              <Area
                dataKey="negative"
                fill="url(#fillSentiment-negative)"
                stroke="var(--color-negative)"
                isAnimationActive={false}
                stackId="a"
                type="bump"
              />
              <Area
                dataKey="positive"
                fill="url(#fillSentiment-positive)"
                stroke="var(--color-positive)"
                isAnimationActive={false}
                stackId="a"
                type="bump"
              />
            </AreaChart>
          </ChartContainer>
        ) : null}
      </WidgetContent>
    </Widget>
  );
}
